import React, { useEffect, useState, FocusEvent, Dispatch, SetStateAction } from "react";
import { Row, Col } from "react-bootstrap";
import { ActionHandlerFunction, PaginationItem, Sort, SortDirection } from "@preveil-api";
import {
  Account, CheckboxStates, CheckboxStatesTypes, DriveLimits, DriveUIActionTypes, GenericEntryEntity, GenericListEntity, GenericSortFieldType, getOppositeSortDirection, SORT_DIRECTION,
  sortBy
} from "src/common";
import { Checkbox, EmptyView, Icon, Loading } from "src/components";
import { ListRow } from "..";
import _ from "lodash";

type AllProps = {
  current_account: Account;
  handleAction: ActionHandlerFunction;
  starred_list: GenericListEntity;
  is_loading: boolean;
  selected: string[];
  pagination: PaginationItem;
  filter?: string;
  setFilterCount: Dispatch<SetStateAction<number>>;
};

function ListViewComponent(props: AllProps) {
  const { current_account, is_loading, starred_list, filter, selected, pagination, handleAction, setFilterCount } = props;
  const [entries, setEntries] = useState<GenericEntryEntity[]>(starred_list.entries);
  const [sort, setSort] = useState<Sort<keyof GenericEntryEntity>>({ field: GenericSortFieldType.last_modification_date, direction: SORT_DIRECTION.descending });

  useEffect(() => {
    // NOTE: Set sorted entries after fetching starred_list && close side panel if opened on navigation
    handleSort(sort.field, sort.direction);
  }, [starred_list]);

  useEffect(() => {
    handleFilter(filter);
  }, [filter]);

  // Description: Filter entries by name or reset if filter is removed
  function handleFilter(search_term?: string) {
    if (!!search_term) {
      const _search_term = search_term.toLowerCase();
      const filtered = _.filter(starred_list.entries, (item: GenericEntryEntity) => item.name.toLowerCase().includes(_search_term));
      setEntries(filtered);
      setFilterCount(filtered.length);
    } else {
      setFilterCount(starred_list.entries.length);
      handleSort(sort.field, sort.direction);
    }
  }

  // Description: Sets the sort field with the new field. (if its the same field, it flips the direction)
  function handleSort(field: keyof GenericEntryEntity, _direction?: SortDirection) {
    const direction = !!_direction ? _direction : (sort.field === field ? getOppositeSortDirection(sort.direction) : SORT_DIRECTION.ascending);
    setSort({ field, direction });
    const sorted_entries = sortBy(starred_list?.entries, { field, direction }, field === GenericSortFieldType.last_modification_date);
    setEntries(sorted_entries);
  }

  // Description: Get the selected all checkbox state
  function getSelectedState(): CheckboxStatesTypes {
      let ck_state: CheckboxStatesTypes = CheckboxStates.empty;
      if (!!pagination) {
          ck_state = (!selected.length ? CheckboxStates.empty :
              (selected.length < DriveLimits.DRIVE_PAGINATION_PAGE_SIZE && selected.length < pagination.totalRows) ?
                  CheckboxStates.indeterminate : CheckboxStates.checked);
      }
      return ck_state;
  }
  
  // Render methods
  // ------------------------------------------------
  // Description: Shareable react fragment - for each field - returns the header with sort icon
  function headerWithSortIcon(name: string, field: string) {
    return (
        <>
            {name}
            {!!sort && sort.field === field && !!entries && entries.length > 1 && (
                <Icon className={`${sort.direction === SORT_DIRECTION.descending ? "ficon-arrow-down" : "ficon-arrow-up"} small-icon ms-1`} />
            )}
        </>
    );
  }

  // Description: Render Header component
  function RenderHeader() {
    return <Row className="header-row">
      <Col md="1">
        <Checkbox className="sr-only"
          onChange={(e: FocusEvent<HTMLInputElement>) => {
            handleAction({ actionType: DriveUIActionTypes.Select, params: { selected: e.target.checked } });
          }}
          value="all" label="Select All" selected={getSelectedState()} />
      </Col>
      <Col md="5" className="col" onClick={() => handleSort(GenericSortFieldType.name)}>
        {headerWithSortIcon("Name", GenericSortFieldType.name)}
      </Col>
      <Col md="2" onClick={() => handleSort(GenericSortFieldType.type_label)}>
        {headerWithSortIcon("Type", GenericSortFieldType.type_label)}
      </Col>
      <Col md="3" onClick={() => handleSort(GenericSortFieldType.last_modification_date)}>
        {headerWithSortIcon("Modified", GenericSortFieldType.last_modification_date)}
      </Col>
      <Col md="1">
        <span hidden>actions</span>
      </Col>
    </Row>;
  }

  // Description: Render the rows
  function RenderList() {
    return (
      <div className="list-body">
        {_.map(entries, (entry: GenericEntryEntity, _i: number) => {
          return <ListRow current_account={current_account} key={entry.id} selected={selected.includes(entry.id)} entry={entry} handleAction={handleAction} />;
        })}
      </div>
    );
  }


  return <div className={`drive-list mypreveil${is_loading ? " isloading" : ""}`}>
    {
      entries.length > 0 ?
        <>
          {RenderHeader()}
          {RenderList()}
        </> :
        <EmptyView message={!filter ? "You have no starred items" : "There are no items matching your search"} />
    }
    {
      is_loading && <Loading />
    }
  </div>;
}

export default React.memo(ListViewComponent);
